import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, X } from 'lucide-react';
import { getProject } from '../services/api';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const data = await getProject(id);
        setProject(data);
      } catch (error) {
        console.error('Error loading project:', error);
        navigate('/portfolio');
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0f10] pt-20 flex justify-center items-center">
        <div className="text-[#4b63eb] text-lg">Loading project...</div>
      </div>
    );
  }

  if (!project) {
    return null;
  }

  const openLightbox = (index) => {
    setSelectedImage(project);
    setCurrentImageIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    setCurrentImageIndex(0);
  };

  const goToNextImage = () => {
    setCurrentImageIndex((prev) => 
      prev < project.images.length - 1 ? prev + 1 : 0
    );
  };

  const goToPrevImage = () => {
    setCurrentImageIndex((prev) => 
      prev > 0 ? prev - 1 : project.images.length - 1
    );
  };

  return (
    <div className="min-h-screen bg-[#0f0f10] pt-20">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/portfolio"
          className="inline-flex items-center space-x-2 text-[#4b63eb] hover:text-[#f5f5f5] transition-colors duration-300"
        >
          <ArrowLeft size={20} />
          <span className="text-sm tracking-widest uppercase">Back to Portfolio</span>
        </Link>
      </div>

      {/* Project Header */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="max-w-3xl">
          <div className="mb-4">
            <span className="inline-block px-4 py-2 bg-gradient-to-r from-[#4b63eb] to-[#3b4ec9] text-[#f5f5f5] text-xs tracking-widest uppercase font-semibold">
              {project.category === 'residential' ? 'Residential Interior' : 'Commercial Exterior'}
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#f5f5f5] mb-6 tracking-wider">
            {project.title}
          </h1>
          <p className="text-lg text-[#a0a0a0] leading-relaxed mb-6">
            {project.description}
          </p>
          <div className="flex flex-wrap gap-6 text-sm">
            <div className="flex items-center space-x-2 text-[#a0a0a0]">
              <MapPin size={18} className="text-[#4b63eb]" />
              <span>{project.location}</span>
            </div>
            <div className="flex items-center space-x-2 text-[#a0a0a0]">
              <Calendar size={18} className="text-[#4b63eb]" />
              <span>{project.year}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Project Images */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {project.images.map((image, index) => (
            <div
              key={index}
              className="group relative overflow-hidden bg-[#1a1a1a] cursor-pointer transition-all duration-300 hover:shadow-xl hover:shadow-[#4b63eb]/20"
              onClick={() => openLightbox(index)}
            >
              <img
                src={image}
                alt={`${project.title} - Image ${index + 1}`}
                className="w-full h-auto transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-[#0f0f10]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <span className="text-[#f5f5f5] text-sm tracking-widest uppercase">View Full Size</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="bg-gradient-to-b from-[#1a1a1a] to-[#0f0f10] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#f5f5f5] mb-4 tracking-wider">
            Interested in Similar Work?
          </h2>
          <p className="text-[#a0a0a0] mb-8">
            Let's discuss how we can capture your architectural vision.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-gradient-to-r from-[#4b63eb] to-[#3b4ec9] text-[#f5f5f5] px-8 py-4 text-sm tracking-widest uppercase font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-[#4b63eb]/50"
          >
            Get In Touch
          </Link>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4">
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-[#f5f5f5] hover:text-[#4b63eb] transition-colors duration-300 z-10"
            aria-label="Close lightbox"
          >
            <X size={32} />
          </button>

          <button
            onClick={goToPrevImage}
            className="absolute left-4 text-[#f5f5f5] hover:text-[#4b63eb] text-4xl font-bold transition-colors duration-300"
            aria-label="Previous image"
          >
            ‹
          </button>

          <div className="max-w-6xl w-full">
            <img
              src={project.images[currentImageIndex]}
              alt={`${project.title} - Image ${currentImageIndex + 1}`}
              className="w-full h-auto max-h-[90vh] object-contain"
            />
          </div>

          <button
            onClick={goToNextImage}
            className="absolute right-4 text-[#f5f5f5] hover:text-[#4b63eb] text-4xl font-bold transition-colors duration-300"
            aria-label="Next image"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;
